import {
  Button,
  Group,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Sparkle } from "lucide-react";
import { useState } from "react";

import {
  getListAdCopiesQueryKey,
  useStartJob,
} from "../../../api/generated/endpoints/generation/generation";
import type { Job, TextAdCopyRequest } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { useIdempotencyKey } from "../../../platform/idempotency";
import type { PickedImage } from "../../../platform/media/ImagePicker";
import type { ListedProduct } from "../../../platform/media/ProductListPopover";
import classes from "./ad-copies.module.css";
import { InspirationField } from "./InspirationField";
import { ProductField } from "./ProductField";
import { useAdStructures } from "./use-ad-structures";

/** Live's choices (`constants/images.tsx`), sent as their words. */
export const TONES = ["Professional", "Casual", "Urgent", "Empathetic", "Bold", "Technical"];
export const AWARENESS = [
  "Unaware",
  "Problem Aware",
  "Solution Aware",
  "Product Aware",
  "Most Aware",
];
export const FUNNEL = ["Awareness", "Consideration", "Decision"];
const COPIES = ["1", "2", "3", "4", "5"];
/** Live's tooltip while the inspiration has no structure. */
export const STRUCTURE_PENDING = "Generation unlocks once the image structure is ready.";

export interface GenerateCopyProps {
  teamId: string;
  brandId: string;
  /** What the form starts with: the draft's product and first reference (live's `utils.ts`). */
  product: ListedProduct | null;
  inspiration: PickedImage | null;
  /** The button's words: live's modal says "Generate", its Ad copy tab "Generate Copy". */
  label?: string;
  /** The start answered: its job drafted the copy `in_progress`, and the list shows it. */
  onStarted: (job: Job) => void;
}

/**
 * Live's AI Generate (`ai-generate-text-copy-form`, captures advanced-234/22 and studio-depth 30,
 * 30b): the product and the inspiration, the brief live's prompt reads, and the number of copies,
 * 3 at first. "Generate" starts `text.ad_copy` (G8), free, and waits for an inspiration's
 * analysis, as live's does.
 */
export function GenerateCopy(props: GenerateCopyProps) {
  const { teamId, brandId, label = "Generate", onStarted } = props;
  const queryClient = useQueryClient();
  const [product, setProduct] = useState(props.product);
  const [inspiration, setInspiration] = useState(props.inspiration);
  const [audience, setAudience] = useState("");
  const [offer, setOffer] = useState("");
  const [tone, setTone] = useState("");
  const [awareness, setAwareness] = useState("");
  const [funnel, setFunnel] = useState("");
  const [currentPrice, setCurrentPrice] = useState("");
  const [originalPrice, setOriginalPrice] = useState("");
  const [currency, setCurrency] = useState("");
  const [savings, setSavings] = useState("");
  const [copies, setCopies] = useState("3");
  const structures = useAdStructures(
    teamId,
    brandId,
    inspiration === null ? [] : [inspiration.assetId],
  );
  const analysis = inspiration === null ? null : structures.of(inspiration.assetId);
  // Live waits for the inspiration's structure; a failed one keeps it waiting until removed.
  const unready =
    analysis !== null && (analysis.state === "analysing" || analysis.structure === null);
  const idempotency = useIdempotencyKey();
  const start = useStartJob({
    mutation: {
      onSuccess: async (job) => {
        await queryClient.invalidateQueries({ queryKey: getListAdCopiesQueryKey(teamId, brandId) });
        onStarted(job);
      },
    },
  });
  const busy = start.isPending;
  const generate = () => {
    if (product === null || unready) return;
    // Live sends every field, an untouched one as "" (G8 PR 2a).
    const data: TextAdCopyRequest = {
      type: "text.ad_copy",
      product_id: product.productId,
      reference: inspiration === null ? null : { kind: "asset", asset_id: inspiration.assetId },
      target_audience: audience,
      discount_offers: offer,
      free_gift_offers: "",
      tone_of_voice: tone,
      awareness_level: awareness,
      funnel_stage: funnel,
      current_price: currentPrice,
      original_price: originalPrice,
      currency,
      savings,
      copies: Number(copies),
    };
    const key = idempotency.keyFor(data);
    start.mutate(
      { teamId, brandId, data, headers: { "Idempotency-Key": key } },
      { onError: (error) => idempotency.failed(key, data, error) },
    );
  };
  const button = (
    <Button
      leftSection={<Sparkle size={16} />}
      loading={busy}
      disabled={product === null || unready}
      onClick={generate}
    >
      {label}
    </Button>
  );
  return (
    <div className={classes.write}>
      <div className={classes.writeBody}>
        <Group grow gap={12} align="flex-start">
          <Stack gap={8}>
            <Text className={classes.sectionTitle}>Select product*</Text>
            <ProductField
              teamId={teamId}
              brandId={brandId}
              product={product}
              disabled={busy}
              onPick={setProduct}
            />
          </Stack>
          <Stack gap={8}>
            <Text className={classes.sectionTitle}>Inspiration</Text>
            <InspirationField
              teamId={teamId}
              brandId={brandId}
              inspiration={inspiration}
              analysing={analysis?.state === "analysing"}
              disabled={busy}
              onPick={setInspiration}
              onRemove={() => setInspiration(null)}
            />
          </Stack>
        </Group>
        <Stack gap={8}>
          <Text className={classes.sectionTitle}>Target Audience</Text>
          <Textarea
            aria-label="Target Audience"
            placeholder="Who is your ideal customer?"
            rows={4}
            value={audience}
            disabled={busy}
            onChange={(event) => setAudience(event.currentTarget.value)}
          />
        </Stack>
        <Stack gap={8}>
          <Text className={classes.sectionTitle}>Offer / Discount</Text>
          <TextInput
            aria-label="Offer / Discount"
            placeholder="e.g. 25% Off, Free Shipping"
            value={offer}
            disabled={busy}
            onChange={(event) => setOffer(event.currentTarget.value)}
          />
        </Stack>
        <Choices
          title="Tone of Voice"
          options={TONES}
          value={tone}
          disabled={busy}
          onChange={setTone}
        />
        <Choices
          title="Awareness Level"
          options={AWARENESS}
          value={awareness}
          disabled={busy}
          onChange={setAwareness}
        />
        <Choices
          title="Funnel Stage"
          options={FUNNEL}
          value={funnel}
          disabled={busy}
          onChange={setFunnel}
        />
        <Stack gap={8}>
          <Text className={classes.sectionTitle}>Pricing</Text>
          <Group grow gap={12} align="flex-start">
            <TextInput
              label="Current Price"
              placeholder="e.g. 29.99"
              value={currentPrice}
              disabled={busy}
              onChange={(event) => setCurrentPrice(event.currentTarget.value)}
            />
            <TextInput
              label="Original Price"
              placeholder="e.g. 49.99"
              value={originalPrice}
              disabled={busy}
              onChange={(event) => setOriginalPrice(event.currentTarget.value)}
            />
            <Select
              label="Currency"
              placeholder="Currency"
              data={["USD"]}
              value={currency === "" ? null : currency}
              disabled={busy}
              onChange={(next) => setCurrency(next ?? "")}
            />
            <TextInput
              label="Savings %"
              placeholder="e.g. 40"
              value={savings}
              disabled={busy}
              onChange={(event) => setSavings(event.currentTarget.value)}
            />
          </Group>
        </Stack>
        <div className={classes.copies}>
          <Text className={classes.sectionTitle}>Copies</Text>
          <Radio.Group value={copies} onChange={setCopies} aria-label="Copies">
            <div className={classes.choices}>
              {COPIES.map((count) => (
                <Radio.Card key={count} value={count} className={classes.choice} disabled={busy}>
                  {count}
                </Radio.Card>
              ))}
            </div>
          </Radio.Group>
        </div>
        {start.isError ? <ErrorAlert error={start.error} /> : null}
      </div>
      <div className={classes.writeFooter}>
        <Tooltip
          label={STRUCTURE_PENDING}
          position="top"
          withArrow
          disabled={!unready || product === null}
        >
          {/* A disabled button takes no hover, so the tooltip hangs from its wrapper. */}
          <span>{button}</span>
        </Tooltip>
      </div>
    </div>
  );
}

interface ChoicesProps {
  title: string;
  options: readonly string[];
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

/** Live's radio cards for one of the brief's choices; none is chosen at first. */
function Choices({ title, options, value, disabled, onChange }: ChoicesProps) {
  return (
    <Stack gap={8}>
      <Text className={classes.sectionTitle}>{title}</Text>
      <Radio.Group value={value} onChange={onChange} aria-label={title}>
        <div className={classes.choices}>
          {options.map((option) => (
            <Radio.Card key={option} value={option} className={classes.choice} disabled={disabled}>
              {option}
            </Radio.Card>
          ))}
        </div>
      </Radio.Group>
    </Stack>
  );
}
